<template>
  <div>
    <PageHeader title="Plan sales">
      <div class="flex flex-wrap items-center gap-2 sm:gap-4">
        <SecondaryButton @click="downloadCSVReport" v-if="isAdminRoute() && hasAccessToAdminDashboard">
          📥 {{ $t("Export") }}
        </SecondaryButton>
        <SecondaryButton @click="$router.push('/pos/leads')" v-else-if="!isAdminRoute() && storeOffline">
          ⏳ Ventes SEPA (via RIB) en attente
        </SecondaryButton>
      </div>
    </PageHeader>
    <InfoBanner
      v-if="showProductExchangeBanner"
      message="Recherchez le contrat, ouvrez sa fiche, puis cliquez sur 🔄 Déclarer un échange de produit."
      variant="info"
      @dismiss="dismissProductExchangeBanner"
    />
    <div>
      <SearchToolbar
        class="mb-6"
        :placeholder="$t('SearchPlanSales')"
        :available-filters="availableFilters"
        :selected-filters="selectedFilters"
        :has-active-period="dateRange.some(Boolean)"
        :initial-keywords="keywords"
        @update-filter-search="updateFilterSearch"
        @update-keyword-search="updateKeywordSearch"
        @clear-filters="dateRangeUpdated([])"
      >
        <DateRangePicker placeholder="Période" v-model="dateRange" @update:modelValue="dateRangeUpdated" />
      </SearchToolbar>
    </div>
    <PlanSaleList
      :current-page="currentPage"
      :plan-sales="index"
      :total-count="totalCount"
      :total-pages="totalPages"
      @updatePage="updatePage"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import PlanSaleList from "@/components/plansales/PlanSaleList.vue";
import { DateRangePicker, InfoBanner, PageHeader, SecondaryButton, SearchToolbar, download } from "@estaly/ui";
import { isAdminRoute } from "@estaly/ui/src/utils/route";

export default {
  components: {
    PageHeader,
    SecondaryButton,
    SearchToolbar,
    DateRangePicker,
    PlanSaleList,
    InfoBanner,
  },

  data: () => {
    return {
      availableFilters: {
        status: [],
        stores: [],
        billing: [],
      },
      selectedFilters: {
        status: [],
        stores: [],
        billing: [],
      },
      dateRange: [],
      startDate: null,
      endDate: null,
      keywords: null,
      currentPage: 1,
    };
  },

  computed: {
    ...mapState("planSales", ["index", "totalCount", "totalPages"]),
    ...mapState("store", { stores: "index", selectedStoreId: "selectedStoreId" }),
    ...mapGetters("planSales", ["statuses"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard"]),
    ...mapGetters("store", ["storeOffline"]),

    monthlyFilter() {
      const [billing] = this.selectedFilters.billing;

      if (this.selectedFilters.billing.length !== 1) return null;

      if (billing === "Monthly") return true;
      if (billing === "Annually") return false;

      return null;
    },

    planSaleParams() {
      return {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        keywords: this.keywords,
        storeIds: this.isAdminRoute() ? this.selectedFilters.stores : [this.selectedStoreId],
        status: this.selectedFilters.status,
        monthly: this.monthlyFilter,
      };
    },

    showProductExchangeBanner() {
      return this.$route.query.action === "productExchange";
    },
  },

  async created() {
    this.availableFilters.status = this.statuses();
    this.availableFilters.billing = ["Monthly", "Annually"];

    if (this.isAdminRoute()) {
      await this.fetchStores({ perPage: 2000, status: [] });
      this.availableFilters.stores = this.stores.map((store) => ({ label: store.salesChannel, value: store.id }));
    }

    await this.retrievePlanSales();
  },

  watch: {
    "$route.query.focusSearch": {
      handler(shouldFocus) {
        if (shouldFocus === "true") {
          this.$nextTick(() => {
            this.focusSearchInput();
            this.$router.replace({
              query: { ...this.$route.query, focusSearch: undefined },
            });
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("planSales", ["fetchIndex", "getPlanSaleCSVExport"]),
    ...mapActions("store", { fetchStores: "fetchIndex", withLoader: "withLoader" }),
    isAdminRoute,

    async retrievePlanSales() {
      await this.withLoader(async () => {
        await this.fetchIndex({
          ...this.planSaleParams,
          page: this.currentPage,
          includes: ["customer", "plan_sale_product", "store", "cancellation"],
        });
      });
    },

    dateRangeUpdated(dateRange) {
      this.dateRange = dateRange;
      this.updatePage(1);
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.updatePage(1);
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.updatePage(1);
    },

    updatePage(page, refresh = true) {
      this.currentPage = page;
      if (refresh) this.retrievePlanSales();
    },

    focusSearchInput() {
      const input = this.$el.querySelector('input[type="text"]');
      if (input) input.focus();
    },

    dismissProductExchangeBanner() {
      const query = { ...this.$route.query };
      delete query.action;
      this.$router.replace({ path: this.$route.path, query });
    },

    async downloadCSVReport() {
      const response = await this.getPlanSaleCSVExport(this.planSaleParams);
      download(response.data.csv_data, response.data.filename);
    },
  },
};
</script>
